import React, { Fragment, useEffect, useState, useMemo } from "react";
import { Container } from "reactstrap";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import Breadcrumb from "../common/breadcrumb";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { Tile, RCard, Badge, Loader } from "../reporting/ReportingShared";
import { getMerchantListing } from "../../utils/apiCalls";
import axiosInstance from "../../utils/axios";

const formatDay = (value) =>
    new Date(value).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

/** Inside the last 30 days of validity: the expiry warnings are already going out. */
const expiresSoon = (value) => !!value && new Date(value).getTime() - Date.now() < 30 * 24 * 60 * 60 * 1000;

// Mirrors resolveTier in the API (src/util/loyaltyUtil.js): highest tier whose
// eligibilityPoints <= the customer's lifetime totalPoints. A tier is no longer a
// stored pointer on UserStores, it is derived — so this has to use the same rule
// the server does, or the admin view will disagree with what customers get.
const resolveTierForPoints = (tiers, totalPoints) => {
    if (!Array.isArray(tiers) || tiers.length === 0) return null;
    const points = Number(totalPoints) || 0;
    const sorted = [...tiers].sort(
        (a, b) => (Number(a.eligibilityPoints) || 0) - (Number(b.eligibilityPoints) || 0)
    );
    let match = null;
    for (const tier of sorted) {
        if ((Number(tier.eligibilityPoints) || 0) <= points) match = tier;
        else break;
    }
    // Below the lowest threshold the customer has reached no tier, so show "—".
    return match;
};

/**
 * Mirrors tierRedeemPointsPerSar in the API. Points-per-SAR is the stored form now; the
 * `pointToCurrency` branch is for a programme not yet re-saved under the new convention,
 * where the same rate is expressed as SAR per point.
 *
 * Getting this wrong is not a cosmetic error: the column below turns a customer's balance
 * into money, and reading a points-per-SAR figure as if it were SAR-per-point overstates
 * the value by the square of the rate.
 */
const redeemPointsPerSarOf = (tier) => {
    if (tier?.redeemPointsPerSar != null && Number(tier.redeemPointsPerSar) > 0) {
        return Number(tier.redeemPointsPerSar);
    }
    if (tier?.pointToCurrency != null && Number(tier.pointToCurrency) > 0) {
        return 1 / Number(tier.pointToCurrency);
    }
    return null;
};

const inputStyle = {
    padding: "10px 14px", borderRadius: 10, fontSize: 13,
    border: `1px solid ${C.border}`, background: C.white,
    color: C.text, outline: "none", transition: "border-color 0.15s",
};

const formatAgo = (iso) => {
    if (!iso) return "";
    const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (sec < 60) return "just now";
    if (sec < 3600) return `${Math.floor(sec / 60)} min ago`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
    return `${Math.floor(sec / 86400)}d ago`;
};

const WalletPassBadge = ({ wp }) => {
    const state = wp?.state || "none";
    if (state === "installed") {
        const n = wp.deviceCount || 0;
        return (
            <span title={wp.lastUpdatedAt ? `Last updated ${new Date(wp.lastUpdatedAt).toLocaleString()}` : ""}>
                <Badge color={C.green} bg={C.greenLight}>● Installed · {n} {n === 1 ? "device" : "devices"}</Badge>
                <span style={{ marginLeft: 8, color: C.textLight, fontSize: 11 }}>{formatAgo(wp.lastUpdatedAt)}</span>
            </span>
        );
    }
    if (state === "generated") {
        return (
            <span title="Pass downloaded but not yet installed on a device">
                <Badge color={C.orange} bg={C.orangeLight}>● Generated</Badge>
                <span style={{ marginLeft: 8, color: C.textLight, fontSize: 11 }}>{formatAgo(wp.lastUpdatedAt)}</span>
            </span>
        );
    }
    return <span style={{ color: C.textLight }}>—</span>;
};

const UserStore = () => {
    const { merchantID } = useParams();
    const userType = localStorage.getItem("userType");
    const loggedInMerchantID = localStorage.getItem("MerchantId");
    const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
    const selectedMerchantId = merchantID || (userType === "merchant" ? loggedInMerchantID : reduxSelectedMerchant);

    const [merchantName, setMerchantName] = useState("");
    const [userStores, setUserStores] = useState([]);
    const [merchantTiers, setMerchantTiers] = useState([]);
    const [expiryEnabled, setExpiryEnabled] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [activeLoyaltyType, setActiveLoyaltyType] = useState("");

    // Merchant name + active loyalty type
    useEffect(() => {
        if (!selectedMerchantId || selectedMerchantId === "all") {
            setMerchantName(selectedMerchantId === "all" ? "All Merchants" : "");
            return;
        }
        (async () => {
            const merchants = await getMerchantListing();
            const found = merchants.find((m) => m.id === parseInt(selectedMerchantId));
            setMerchantName(found?.name || "Unknown Merchant");
        })();
    }, [selectedMerchantId]);

    // Tiers no longer need their own request: the ladder lives on the loyalty
    // program itself (MerchantLoyalty.percentageTiers) and getAlluserStores
    // already includes the active loyalty, so it arrives with the stores below.

    // Stores
    useEffect(() => {
        if (!selectedMerchantId || selectedMerchantId === "all") {
            setUserStores([]); setActiveLoyaltyType(""); return;
        }
        (async () => {
            setIsFetching(true);
            try {
                const response = await axiosInstance.get("/api/getAlluserStores");
                const all = response.data.data || [];
                const filtered = all.filter((s) => s.MerchantId === parseInt(selectedMerchantId));
                setUserStores(filtered);
                if (filtered.length > 0) {
                    const active = filtered[0].Merchant?.loyalties?.find((l) => l.isActive);
                    setActiveLoyaltyType(active?.loyaltyType || "");
                    setMerchantTiers(Array.isArray(active?.percentageTiers) ? active.percentageTiers : []);
                    setExpiryEnabled(!!active?.pointsExpiryEnabled);
                } else {
                    setActiveLoyaltyType("");
                    setMerchantTiers([]);
                    setExpiryEnabled(false);
                }
            } catch (e) {
                console.error("Error fetching user stores:", e);
                setUserStores([]); setActiveLoyaltyType(""); setMerchantTiers([]); setExpiryEnabled(false);
            } finally {
                setIsFetching(false);
            }
        })();
    }, [selectedMerchantId]);

    const filteredStores = useMemo(() => {
        if (!searchQuery.trim()) return userStores;
        const q = searchQuery.toLowerCase();
        return userStores.filter((s) => {
            const name = `${s.User?.firstName || ""} ${s.User?.lastName || ""}`.toLowerCase();
            const phone = (s.User?.phoneNo || "").toLowerCase();
            return name.includes(q) || phone.includes(q);
        });
    }, [userStores, searchQuery]);

    const tiles = useMemo(() => {
        const list = userStores;
        const totalCustomers = list.length;
        const totalStamps = list.reduce((s, x) => s + (x.lifetimeStamps || 0), 0);
        const totalRewards = list.reduce((s, x) => s + (x.rewardsAvailable || 0), 0);
        const installed = list.filter((x) => x.walletPass?.state === "installed").length;
        const generated = list.filter((x) => x.walletPass?.state === "generated").length;
        const installRate = totalCustomers > 0 ? Math.round((installed / totalCustomers) * 100) : 0;
        return { totalCustomers, totalStamps, totalRewards, installed, generated, installRate };
    }, [userStores]);

    const isStamps = activeLoyaltyType === "stamps";
    const isPercentage = activeLoyaltyType === "percentage";

    const noMerchant = !selectedMerchantId || selectedMerchantId === "all";

    return (
        <Fragment>
            <Breadcrumb title="User Store" parent={merchantID ? "Merchants" : "Loyalty"} />
            <Container fluid>
                <style>{reportingGlobalCSS}</style>
                <div className="rpt-wrap">

                    {/* Tiles */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 20 }}>
                        <Tile label="Customers" value={tiles.totalCustomers} sub={merchantName ? `at ${merchantName}` : "Enrolled"} icon="👥" gradient={`linear-gradient(135deg, ${C.coral}, #00bfa5)`} delay={0} />
                        {isStamps ? (
                            <Tile label="Lifetime Stamps" value={tiles.totalStamps} sub="Across all customers" icon="⭐" gradient={`linear-gradient(135deg, ${C.orange}, #FFD200)`} delay={80} />
                        ) : (
                            <Tile label="Loyalty Mode" value={isPercentage ? "Points" : "—"} sub={isPercentage ? "Percentage based" : "Not set"} icon="💎" gradient={`linear-gradient(135deg, ${C.blue}, #A8B8FF)`} delay={80} />
                        )}
                        <Tile label="Rewards Available" value={tiles.totalRewards} sub="Unredeemed across customers" icon="🎁" gradient={`linear-gradient(135deg, ${C.green}, #38F9D7)`} delay={160} />
                        <Tile label="Wallet Adoption" value={`${tiles.installRate}%`} sub={`${tiles.installed} installed · ${tiles.generated} generated`} icon="📲" gradient={`linear-gradient(135deg, ${C.purple}, #C39BD3)`} delay={240} />
                    </div>

                    {/* Filter row */}
                    <RCard>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
                            <input
                                type="search"
                                placeholder="Search by name or phone…"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                style={{ ...inputStyle, minWidth: 280, flex: 1 }}
                            />
                            <span style={{ fontSize: 12, color: C.textMed }}>
                                Showing <strong style={{ color: C.text }}>{filteredStores.length}</strong> of {tiles.totalCustomers}
                            </span>
                        </div>
                    </RCard>

                    {/* Table */}
                    <RCard
                        title={
                            <span>👥 User Stores {merchantName && <span style={{ color: C.textLight, fontWeight: 500, marginLeft: 8 }}>· {merchantName}</span>}</span>
                        }
                    >
                        {noMerchant && !merchantID ? (
                            <div style={{ textAlign: "center", padding: 40, color: C.textMed }}>Please select a merchant.</div>
                        ) : isFetching ? (
                            <Loader />
                        ) : userStores.length === 0 ? (
                            <div style={{ textAlign: "center", padding: 40, color: C.textMed }}>
                                No customer stores found for {merchantName || "this merchant"}.
                            </div>
                        ) : filteredStores.length === 0 ? (
                            <div style={{ textAlign: "center", padding: 40, color: C.textMed }}>No matches for that search.</div>
                        ) : (
                            <div style={{ overflowX: "auto" }}>
                                <table style={tbl.table}>
                                    <thead>
                                        <tr>
                                            <th style={tbl.th}>ID</th>
                                            <th style={tbl.th}>User</th>
                                            {!isStamps && <th style={tbl.th}>Tier</th>}
                                            {!isStamps && <th style={tbl.th}>Total Points</th>}
                                            {!isStamps && <th style={tbl.th}>Available Points</th>}
                                            {isPercentage && expiryEnabled && <th style={tbl.th}>Points Expire</th>}
                                            {!isPercentage && <th style={tbl.th}>Lifetime Stamps</th>}
                                            {!isPercentage && <th style={tbl.th}>Stamps Balance</th>}
                                            {!isPercentage && <th style={tbl.th}>Lifetime Rewards</th>}
                                            {!isPercentage && <th style={tbl.th}>Rewards Balance</th>}
                                            <th style={tbl.th}>Redeem Rate</th>
                                            <th style={tbl.th}>Points Value</th>
                                            <th style={tbl.th}>Wallet Pass</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredStores.map((store) => {
                                            const tier = resolveTierForPoints(merchantTiers, store.totalPoints);
                                            const fullName = store.User
                                                ? `${store.User.firstName || ""} ${store.User.lastName || ""}`.trim() || "Unknown"
                                                : "Unknown";
                                            return (
                                                <tr key={store.id} className="rpt-row">
                                                    <td style={{ ...tbl.td, color: C.textMed, fontSize: 12 }}>#{store.id}</td>
                                                    <td style={tbl.td}>
                                                        <div style={{ fontWeight: 600, color: C.text }}>{fullName}</div>
                                                        {store.User?.phoneNo && (
                                                            <div style={{ fontSize: 11, color: C.textLight, marginTop: 2 }}>{store.User.phoneNo}</div>
                                                        )}
                                                    </td>
                                                    {!isStamps && (
                                                        <td style={tbl.td}>
                                                            {tier ? <Badge color={C.purple} bg={C.purpleLight}>{tier.tierName}</Badge> : <span style={{ color: C.textLight }}>—</span>}
                                                        </td>
                                                    )}
                                                    {!isStamps && <td style={{ ...tbl.td, fontWeight: 600 }}>{store.totalPoints || 0}</td>}
                                                    {!isStamps && <td style={{ ...tbl.td, color: C.green, fontWeight: 600 }}>{store.rewardPoints || 0}</td>}
                                                    {isPercentage && expiryEnabled && (
                                                        <td style={{ ...tbl.td, fontSize: 12, color: expiresSoon(store.pointsExpiresAt) ? C.orange : C.textMed }}>
                                                            {store.pointsExpiresAt ? formatDay(store.pointsExpiresAt) : "—"}
                                                        </td>
                                                    )}
                                                    {!isPercentage && <td style={{ ...tbl.td, fontWeight: 600 }}>{store.lifetimeStamps || 0}</td>}
                                                    {!isPercentage && <td style={{ ...tbl.td, color: C.orange, fontWeight: 600 }}>{store.stampsBalance || 0}</td>}
                                                    {!isPercentage && <td style={{ ...tbl.td, fontWeight: 600 }}>{store.rewardsEarned || 0}</td>}
                                                    {!isPercentage && (
                                                        <td style={tbl.td}>
                                                            {(store.rewardsAvailable || 0) > 0 ? (
                                                                <Badge color={C.green} bg={C.greenLight}>{store.rewardsAvailable} ready</Badge>
                                                            ) : (
                                                                <span style={{ color: C.textLight }}>0</span>
                                                            )}
                                                        </td>
                                                    )}
                                                    <td style={{ ...tbl.td, color: C.textMed, fontSize: 12 }}>
                                                        {redeemPointsPerSarOf(tier)
                                                            ? `${store.totalPoints || 0} ÷ ${redeemPointsPerSarOf(tier)}`
                                                            : <span style={{ color: C.textLight }}>—</span>}
                                                    </td>
                                                    <td style={{ ...tbl.td, fontWeight: 600 }}>
                                                        {redeemPointsPerSarOf(tier)
                                                            ? ((store.totalPoints || 0) / redeemPointsPerSarOf(tier)).toFixed(2)
                                                            : <span style={{ color: C.textLight }}>—</span>}
                                                    </td>
                                                    <td style={tbl.td}><WalletPassBadge wp={store.walletPass} /></td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </RCard>
                </div>
            </Container>
        </Fragment>
    );
};

export default UserStore;
